import React from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { useToast } from 'containers/ToastManager'

import Input from 'components/Input'

import {
  Wrapper,
  Name,
  Form,
  Button,
} from './WorkspaceSettingsDetailsPanel.styles'

import WorkspaceLogo from '../WorkspaceLogo'
import { WorkspaceSettingsLogoUpload } from '../WorkspaceSettingsLogoUpload'

import {
  useUpdateWorkspaceMutation,
  useDeleteWorkspaceAvatarMutation,
} from 'gqlTypes'

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required("The workspace name can't be blank"),
})

interface WorkspaceSettingsDetailsPanelProps {
  name: string
  id: string
  avatar?: string
  isUserAdministrator?: boolean
}

export const WorkspaceSettingsDetailsPanel: React.FC<WorkspaceSettingsDetailsPanelProps> = props => {
  const { name, id, avatar, isUserAdministrator } = props

  const { showToast } = useToast()

  const [updateWorkspace] = useUpdateWorkspaceMutation({
    onError: 'show-toast',
    onCompleted: () => {
      showToast('Workspace renamed')
    },
  })

  const [updateAvatar] = useUpdateWorkspaceMutation({
    onError: 'show-toast',
    onCompleted: () => {
      showToast('New Workspace image added')
    },
  })

  const [deleteWorkspace] = useDeleteWorkspaceAvatarMutation({
    variables: { workspaceId: id },
    onError: 'show-toast',
    onCompleted: () => {
      showToast('Workspace image removed', 'positive')
    },
  })

  if (isUserAdministrator) {
    return (
      <Wrapper>
        <WorkspaceSettingsLogoUpload
          workspaceName={name}
          logo={avatar}
          onDrop={async avatar => {
            await updateAvatar({
              variables: {
                input: {
                  identifier: id,
                  avatar,
                },
              },
            })
          }}
          onRemove={async () => {
            await deleteWorkspace()
          }}
          onError={message => {
            showToast(message, 'negative')
          }}
        />

        <Formik
          initialValues={{ name }}
          onSubmit={async (values, actions) => {
            const { name } = validationSchema.cast(values)

            await updateWorkspace({
              variables: {
                input: {
                  identifier: id,
                  name,
                },
              },
            })

            actions.resetForm()
          }}
          validationSchema={validationSchema}
          enableReinitialize>
          {({
            values,
            handleChange,
            dirty,
            errors,
            touched,
            isValid,
            isSubmitting,
          }) => (
            <Form>
              <Form.Field
                mb={0}
                name="text"
                label="Name"
                errorText={touched.name ? errors.name : undefined}>
                <Input
                  name="name"
                  type="text"
                  value={values.name}
                  onChange={handleChange}
                />
              </Form.Field>
              <Button
                variant="secondary"
                small
                type="submit"
                disabled={!dirty || !isValid}
                loading={isSubmitting}>
                Save
              </Button>
            </Form>
          )}
        </Formik>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <WorkspaceLogo size="64px" workspaceName={name} src={avatar} />
      <Name>{name}</Name>
    </Wrapper>
  )
}
